import React, { useState } from "react";
import {
  Users,
  Globe,
  Target,
  MapPin,
  CheckCircle,
  ArrowRight,
  Shield,
  Plus,
  Info,
} from "lucide-react";
import campaignData from "../../data/campaignData.json";

// Hardcoded logic to map personas to Buyer Needs
const buyerNeedsMapping = {
  "L&D Leader": {
    primary: "Workforce upskilling",
    secondary: "Proving ROI of learning",
  },
  "HR Director": {
    primary: "Retention & Culture",
    secondary: "Cost optimization",
  },
  "IT/Ops": {
    primary: "Integration Security",
    secondary: "Scalable deployment",
  },
  "Individual Learner": {
    primary: "Career advancement",
    secondary: "Skill certification",
  },
  "CTO / Tech Lead": {
    primary: "Tech modernization",
    secondary: "Speed to deployment",
  },
};

// Region-to-Vertical Mapping Logic
const regionVerticals = {
  India: ["Technology", "Financial Services"],
  SEA: ["Financial Services"],
};

const SegmentationCard = ({ options, onGenerate }) => {
  const narrative = campaignData.tab_narratives?.segmentation;

  // Default State
  const [selected, setSelected] = useState({
    region: "India",
    vertical: "Technology",
    persona: "L&D Leader",
    stage: "Awareness",
  });

  // Calculate current need based on selection
  const currentNeeds = buyerNeedsMapping[selected.persona] || {
    primary: "General Education",
    secondary: "Brand Awareness",
  };

  // Handler to update region AND reset vertical to a valid option
  const handleRegionChange = (newRegion) => {
    const validVerticals = regionVerticals[newRegion];
    setSelected({
      ...selected,
      region: newRegion,
      vertical: validVerticals[0], // Default to first valid vertical
    });
  };

  const handleGenerate = () => {
    const segmentName = `${selected.region}_${selected.vertical}_${selected.persona}_${selected.stage}`;

    if (onGenerate) {
      onGenerate({
        id: Math.random().toString(36).substr(2, 9),
        name: segmentName,
        needs: currentNeeds,
        ...selected,
      });
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 font-sans">
      {/* 1. NARRATIVE PANEL */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="bg-slate-50 p-6 border-b border-slate-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 text-blue-700 rounded-lg">
              <Users size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                {narrative?.title || "Segmentation Logic"}
              </h2>
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">
                {narrative?.subtitle || "Strategic Definition"}
              </p>
            </div>
          </div>
          <p className="text-slate-700 mt-2 max-w-4xl text-lg leading-relaxed">
            {narrative?.executive_overview ||
              "Define who enters the system. This layer establishes strategic boundaries to ensure relevance."}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100">
          <div className="p-6">
            <h4 className="font-bold text-slate-900 text-sm mb-3 flex items-center gap-2">
              <CheckCircle size={16} className="text-emerald-500" /> What You
              Get
            </h4>
            <ul className="space-y-2">
              {narrative?.what_you_get?.map((item, i) => (
                <li
                  key={i}
                  className="text-sm text-slate-600 flex items-start gap-2"
                >
                  <span className="mt-1.5 w-1 h-1 bg-slate-400 rounded-full shrink-0"></span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="p-6 bg-slate-50/50">
            <h4 className="font-bold text-slate-900 text-sm mb-3 flex items-center gap-2">
              <Shield size={16} className="text-amber-500" /> Why It Matters
            </h4>
            <p className="text-sm text-slate-600 leading-relaxed">
              {narrative?.why_it_matters}
            </p>
          </div>
          <div className="p-6">
            <h4 className="font-bold text-slate-900 text-sm mb-3">
              Distinction
            </h4>
            <p className="text-sm text-slate-500 italic border-l-2 border-blue-200 pl-3">
              "{narrative?.difference}"
            </p>
          </div>
        </div>
      </div>

      {/* 2. FUNCTIONAL BUILDER */}
      <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-bold text-slate-900 text-lg">
            Define Buying Group
          </h3>
          <span className="text-xs font-mono text-slate-400 bg-slate-100 px-2 py-1 rounded">
            Input Configuration
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Form Controls */}
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Region */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500 uppercase flex items-center gap-2">
                <Globe size={14} /> Region Context
              </label>
              <select
                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 focus:ring-2 focus:ring-blue-500 outline-none"
                value={selected.region}
                onChange={(e) => handleRegionChange(e.target.value)}
              >
                <option value="India">India</option>
                <option value="SEA">SEA</option>
              </select>
            </div>

            {/* Vertical (Dynamic based on Region) */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500 uppercase flex items-center gap-2">
                <MapPin size={14} /> Vertical Industry
              </label>
              <select
                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 focus:ring-2 focus:ring-blue-500 outline-none"
                value={selected.vertical}
                onChange={(e) =>
                  setSelected({ ...selected, vertical: e.target.value })
                }
              >
                {regionVerticals[selected.region].map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>

            {/* Persona */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500 uppercase flex items-center gap-2">
                <Users size={14} /> Buying Role
              </label>
              <select
                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 focus:ring-2 focus:ring-blue-500 outline-none"
                value={selected.persona}
                onChange={(e) =>
                  setSelected({ ...selected, persona: e.target.value })
                }
              >
                <option>L&D Leader</option>
                <option>HR Director</option>
                <option>CTO / Tech Lead</option>
                <option>IT/Ops</option>
              </select>
            </div>

            {/* Stage */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500 uppercase flex items-center gap-2">
                <Target size={14} /> Journey Stage
              </label>
              <select
                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 focus:ring-2 focus:ring-blue-500 outline-none"
                value={selected.stage}
                onChange={(e) =>
                  setSelected({ ...selected, stage: e.target.value })
                }
              >
                <option>Awareness</option>
                <option>Consideration</option>
                <option>Decision</option>
              </select>
            </div>
          </div>

          {/* Right Column: Strategic Preview */}
          <div className="lg:col-span-1">
            <div className="bg-purple-50 p-5 rounded-xl border border-purple-100 h-full flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Target size={16} className="text-purple-600" />
                  <span className="text-xs font-bold text-purple-700 uppercase tracking-wide">
                    Identified Buyer Need
                  </span>
                </div>
                <h4 className="text-lg font-bold text-purple-900 leading-tight mb-2">
                  {currentNeeds.primary}
                </h4>
                <p className="text-sm text-purple-700 opacity-80">
                  Secondary: {currentNeeds.secondary}
                </p>
              </div>

              <div className="mt-6 pt-6 border-t border-purple-100">
                <button
                  onClick={handleGenerate}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-lg flex items-center justify-center gap-2 font-bold transition-all shadow-md hover:shadow-lg text-sm"
                >
                  <Plus size={18} /> Add Segment to Strategy
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SegmentationCard;
